import { Box, IconButton } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import React, {
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { AtomicLogo } from "../../assets/AtomicLogo";
import {
  isRejectedNucleusExplainActivation,
  NUCLEUS_EXPLAIN_BUSY_REASON,
  NUCLEUS_EXPLAIN_STARTING_REASON,
  NUCLEUS_EXPLAIN_START_FAILED_REASON,
  type NucleusExplainActivation,
  type NucleusExplainActivationHandler,
  type NucleusExplainActivationOutcome,
} from "../../entities/nucleus_explain_action";

const ACTION_SIZE = 20;
const TOOLTIP_WIDTH = 196;
const TOOLTIP_HEIGHT = 48;
const HOVER_DELAY_MS = 150;
const LEAVE_GRACE_MS = 300;
const MIN_VISIBLE_RATIO = 0.6;
const ACTION_CONTENT_GAP = 1;
const TOOLTIP_GAP = 8;
const NUCLEUS_BLUE = "#3877F7";
const NUCLEUS_EXPLAIN_ACTIVATE_EVENT = "nucleus-explain-activate";

type Rect = Pick<
  DOMRect,
  "left" | "right" | "top" | "bottom" | "width" | "height"
>;

export type NucleusExplainDetailMode =
  | "trace"
  | "verify"
  | "proposal"
  | "explain";

type RegisteredAction = Readonly<{
  actionId: string;
  ariaLabel: string;
  detailMode: NucleusExplainDetailMode;
  displayValue?: string;
  identityKey: string;
  onExplain: NucleusExplainActivationHandler;
  disabled: boolean;
}>;

type RegisteredActionReader = () => RegisteredAction;

const registeredActions = new WeakMap<HTMLElement, RegisteredActionReader>();

const isActivationPromise = (
  value: NucleusExplainActivation,
): value is Promise<NucleusExplainActivationOutcome> =>
  value != null &&
  typeof value === "object" &&
  "then" in value &&
  typeof value.then === "function";

const makeRect = (
  left: number,
  top: number,
  width: number,
  height: number,
): Rect => ({
  left,
  right: left + width,
  top,
  bottom: top + height,
  width,
  height,
});

const clamp = (value: number, minimum: number, maximum: number): number =>
  Math.max(minimum, Math.min(maximum, value));

const textRectsFor = (cell: HTMLElement): Rect[] => {
  const doc = cell.ownerDocument;
  const view = doc.defaultView;
  const containingCell = cell.closest<HTMLElement>("th, td") ?? cell;
  const containingCellRect = containingCell.getBoundingClientRect();
  const showText = view?.NodeFilter?.SHOW_TEXT ?? 4;
  const walker = doc.createTreeWalker(cell, showText);
  const rects: Rect[] = [];
  let node = walker.nextNode();
  while (node) {
    const text = node.textContent?.trim();
    const parent = node.parentElement;
    if (
      text &&
      parent &&
      !parent.closest(
        "button,[aria-hidden='true'],[data-nucleus-ignore-content='true']",
      )
    ) {
      const range = doc.createRange();
      range.selectNodeContents(node);
      const clientRects =
        typeof range.getClientRects === "function"
          ? range.getClientRects()
          : [];
      for (const item of Array.from(clientRects)) {
        const left = Math.max(item.left, containingCellRect.left);
        const right = Math.min(item.right, containingCellRect.right);
        const top = Math.max(item.top, containingCellRect.top);
        const bottom = Math.min(item.bottom, containingCellRect.bottom);
        if (right > left && bottom > top) {
          rects.push(makeRect(left, top, right - left, bottom - top));
        }
      }
      range.detach();
    }
    node = walker.nextNode();
  }
  return rects;
};

// The hover-revealed trigger every cell-detail popover renders on the leading
// edge of its cell (chunked matrix, Supply and Demand pivots).
const CELL_DRILLDOWN_SELECTOR = ".cellDrilldownBtn";

const NESTED_VALUE_CONTROL_SELECTOR = [
  "input:not([type='hidden'])",
  "textarea",
  "select",
  "[contenteditable]:not([contenteditable='false'])",
  "[role='textbox']",
  "[role='spinbutton']",
  "[role='combobox']",
].join(",");

const controlRectsFor = (cell: HTMLElement): Rect[] => {
  const containingCell = cell.closest<HTMLElement>("th, td") ?? cell;
  const containingCellRect = containingCell.getBoundingClientRect();
  const view = cell.ownerDocument.defaultView;
  const rects: Rect[] = [];

  for (const control of cell.querySelectorAll<HTMLElement>(
    NESTED_VALUE_CONTROL_SELECTOR,
  )) {
    if (
      control.closest(
        "[aria-hidden='true'],[data-nucleus-ignore-content='true']",
      )
    ) {
      continue;
    }
    const style = view?.getComputedStyle(control);
    if (style?.display === "none" || style?.visibility === "hidden") continue;
    const item = control.getBoundingClientRect();
    const left = Math.max(item.left, containingCellRect.left);
    const right = Math.min(item.right, containingCellRect.right);
    const top = Math.max(item.top, containingCellRect.top);
    const bottom = Math.min(item.bottom, containingCellRect.bottom);
    if (right > left && bottom > top) {
      rects.push(makeRect(left, top, right - left, bottom - top));
    }
  }

  return rects;
};

type ContentGeometry = Readonly<{
  rect: Rect;
  hasValueControl: boolean;
}>;

const contentGeometryFor = (cell: HTMLElement): ContentGeometry | null => {
  const controlRects = controlRectsFor(cell);
  const rects = [...textRectsFor(cell), ...controlRects];
  if (rects.length === 0) return null;
  const left = Math.min(...rects.map((item) => item.left));
  const right = Math.max(...rects.map((item) => item.right));
  const top = Math.min(...rects.map((item) => item.top));
  const bottom = Math.max(...rects.map((item) => item.bottom));
  return {
    rect: makeRect(left, top, right - left, bottom - top),
    hasValueControl: controlRects.length > 0,
  };
};

const contentRectFor = (cell: HTMLElement): Rect | null =>
  contentGeometryFor(cell)?.rect ?? null;

const rowGeometryFor = (
  root: HTMLElement,
  cell: HTMLElement,
  cellRect: Rect,
): Readonly<{
  precedingContentRect: Rect | null;
  stickyBoundary: number;
}> => {
  let stickyBoundary = root.getBoundingClientRect().left;
  let precedingContentRect: Rect | null = null;
  let precedingRight = Number.NEGATIVE_INFINITY;

  const row = cell.closest<HTMLTableRowElement>("tr");
  const candidates: HTMLElement[] = row ? Array.from(row.cells) : [cell];

  for (const candidate of candidates) {
    const rect =
      candidate === cell ? cellRect : candidate.getBoundingClientRect();

    if (getComputedStyle(candidate).position === "sticky") {
      stickyBoundary = Math.max(stickyBoundary, rect.right);
    }
    if (
      !candidate.contains(cell) &&
      rect.right <= cellRect.left + 0.5 &&
      rect.right > precedingRight
    ) {
      const candidateContent = contentRectFor(candidate);
      if (candidateContent) {
        precedingContentRect = candidateContent;
        precedingRight = rect.right;
      }
    }
  }

  return { precedingContentRect, stickyBoundary };
};

type Placement = Readonly<{
  actionRect: Rect;
  outlineRect: Rect;
  tooltipRect: Rect;
}>;

const computePlacement = ({
  cellRect,
  contentRect,
  precedingContentRect,
  tableRect,
  stickyBoundary,
  allowTrailingPlacement = false,
  leadingLaneOccupied = false,
}: Readonly<{
  cellRect: Rect;
  contentRect: Rect;
  precedingContentRect: Rect | null;
  tableRect: Rect;
  stickyBoundary: number;
  allowTrailingPlacement?: boolean;
  /**
   * The cell has its own leading-edge control — a cell-detail drilldown (PO /
   * sales sources / sales orders popover), which is also an Atomic logo in a
   * white circle revealed on the same hover. Two identical pucks stacked in
   * one lane are indistinguishable and this overlay would take the click, so
   * Explain moves to the trailing edge for such cells.
   */
  leadingLaneOccupied?: boolean;
}>): Placement | null => {
  if (cellRect.width <= 0 || cellRect.height <= 0) return null;

  const visibleLeft = Math.max(cellRect.left, tableRect.left, stickyBoundary);
  const visibleRight = Math.min(cellRect.right, tableRect.right);
  const visibleWidth = Math.max(0, visibleRight - visibleLeft);
  if (visibleWidth / cellRect.width < MIN_VISIBLE_RATIO) return null;
  if (
    contentRect.left < Math.max(tableRect.left, stickyBoundary) ||
    contentRect.right > tableRect.right
  ) {
    return null;
  }

  const outlineTop = Math.max(cellRect.top, tableRect.top);
  const outlineHeight =
    Math.min(cellRect.bottom, tableRect.bottom) - outlineTop;
  if (outlineHeight <= 0) return null;

  const verifiedGutterMinimum = precedingContentRect
    ? Math.max(tableRect.left, precedingContentRect.right + ACTION_CONTENT_GAP)
    : visibleLeft;
  const actionMinimum =
    cellRect.left < stickyBoundary
      ? Math.max(stickyBoundary, verifiedGutterMinimum)
      : verifiedGutterMinimum;
  const leadingActionMaximum = Math.min(
    contentRect.left - ACTION_CONTENT_GAP - ACTION_SIZE,
    visibleRight - ACTION_SIZE,
  );
  let actionLeft: number;
  if (leadingLaneOccupied) {
    // Mirror of the leading placement: straddle the cell's RIGHT border, or
    // sit just past the value when the value reaches that far.
    const trailingMaximum = tableRect.right - ACTION_SIZE;
    if (trailingMaximum < visibleLeft) return null;
    actionLeft = Math.min(
      Math.max(
        contentRect.right + ACTION_CONTENT_GAP,
        cellRect.right - ACTION_SIZE / 2,
      ),
      trailingMaximum,
    );
  } else if (leadingActionMaximum >= actionMinimum) {
    const preferredActionLeft = cellRect.left - ACTION_SIZE / 2;
    actionLeft = clamp(
      preferredActionLeft,
      actionMinimum,
      leadingActionMaximum,
    );
  } else {
    if (!allowTrailingPlacement) return null;
    const trailingActionMinimum = Math.max(
      visibleLeft,
      contentRect.right + ACTION_CONTENT_GAP,
    );
    const trailingActionMaximum = visibleRight - ACTION_SIZE;
    if (trailingActionMaximum < trailingActionMinimum) return null;
    actionLeft = clamp(
      contentRect.right + ACTION_CONTENT_GAP,
      trailingActionMinimum,
      trailingActionMaximum,
    );
  }
  const actionTop = cellRect.top + (cellRect.height - ACTION_SIZE) / 2;
  const tooltipMinimum = tableRect.left + 8;
  const tooltipMaximum = Math.max(
    tooltipMinimum,
    tableRect.right - TOOLTIP_WIDTH - 8,
  );
  const tooltipLeft = clamp(
    actionLeft + ACTION_SIZE / 2 - TOOLTIP_WIDTH / 2,
    tooltipMinimum,
    tooltipMaximum,
  );
  const tooltipFitsAbove =
    actionTop - TOOLTIP_GAP - TOOLTIP_HEIGHT >= tableRect.top + 4;
  const tooltipTop = tooltipFitsAbove
    ? actionTop - TOOLTIP_GAP - TOOLTIP_HEIGHT
    : actionTop + ACTION_SIZE + TOOLTIP_GAP;

  return {
    actionRect: makeRect(actionLeft, actionTop, ACTION_SIZE, ACTION_SIZE),
    outlineRect: makeRect(visibleLeft, outlineTop, visibleWidth, outlineHeight),
    tooltipRect: makeRect(
      tooltipLeft,
      tooltipTop,
      TOOLTIP_WIDTH,
      TOOLTIP_HEIGHT,
    ),
  };
};

export type NucleusExplainCellProps = Readonly<{
  children: ReactNode;
  ariaLabel: string;
  detailMode?: NucleusExplainDetailMode;
  displayValue?: string;
  /** Stable row/cell identity under virtualization. */
  identityKey?: string;
  onExplain: NucleusExplainActivationHandler;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}>;

/**
 * Pointer-passive cell marker. Pointer users invoke only the overlay icon;
 * keyboard users retain an explicit Enter/Space shortcut.
 */
export const NucleusExplainCell: React.FC<NucleusExplainCellProps> = ({
  children,
  ariaLabel,
  detailMode = "explain",
  displayValue,
  identityKey = ariaLabel,
  onExplain,
  disabled = false,
  sx,
}) => {
  const elementRef = useRef<HTMLElement | null>(null);
  const actionId = useId();
  const actionRef = useRef<RegisteredAction>({
    actionId,
    ariaLabel,
    detailMode,
    displayValue,
    identityKey,
    onExplain,
    disabled,
  });
  actionRef.current = {
    actionId,
    ariaLabel,
    detailMode,
    displayValue,
    identityKey,
    onExplain,
    disabled,
  };

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    const reader: RegisteredActionReader = () => actionRef.current;
    registeredActions.set(element, reader);
    return () => {
      if (registeredActions.get(element) === reader) {
        registeredActions.delete(element);
      }
    };
  }, []);

  return (
    <Box
      ref={elementRef}
      data-nucleus-explain-cell="true"
      data-testid="nucleus-explain-cell"
      role="group"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-keyshortcuts="Enter Space"
      aria-disabled={disabled || undefined}
      onKeyDown={(event) => {
        if (
          event.target !== event.currentTarget ||
          (event.key !== "Enter" && event.key !== " ")
        ) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.dispatchEvent(
          new Event(NUCLEUS_EXPLAIN_ACTIVATE_EVENT, { bubbles: true }),
        );
      }}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        minWidth: 0,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

type Anchor = Readonly<{
  marker: HTMLElement;
  cell: HTMLElement;
  readAction: RegisteredActionReader;
  identityKey: string;
  displayValue: string;
}>;

const findExplainMarker = (
  root: HTMLElement,
  target: EventTarget | null,
): HTMLElement | null => {
  if (!(target instanceof Element)) return null;
  const directMarker = target.closest<HTMLElement>(
    "[data-nucleus-explain-cell='true']",
  );
  const tableCell = target.closest<HTMLElement>("td");
  const marker =
    directMarker ??
    tableCell?.querySelector<HTMLElement>(
      "[data-nucleus-explain-cell='true']",
    ) ??
    null;
  return marker && root.contains(marker) ? marker : null;
};

const anchorCellFor = (marker: HTMLElement): HTMLElement =>
  marker.closest<HTMLElement>("td") ?? marker;

const detailFor = (
  mode: NucleusExplainDetailMode,
  displayValue: string,
): string => {
  switch (mode) {
    case "trace":
      return `Trace what built ${displayValue}.`;
    case "verify":
      return `Verify the saved value ${displayValue}.`;
    case "proposal":
      return "Explain why this saved proposal needs review.";
    default:
      return `Explain ${displayValue} and the available evidence.`;
  }
};

export const NucleusExplainOverlay: React.FC<{
  rootRef: RefObject<HTMLElement | null>;
}> = ({ rootRef }) => {
  const tooltipId = useId();
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [activationPending, setActivationPending] = useState(false);
  const [rejectionReason, setRejectionReason] = useState<string | null>(null);
  const anchorRef = useRef<Anchor | null>(null);
  const activationPendingRef = useRef(false);
  const activationGeneration = useRef(0);
  const showTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingCell = useRef<HTMLElement | null>(null);

  const clearTimer = (
    timer: React.MutableRefObject<ReturnType<typeof setTimeout> | null>,
  ) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  const updateAnchor = useCallback((next: Anchor | null) => {
    setTooltipVisible(false);
    activationPendingRef.current = false;
    setActivationPending(false);
    setRejectionReason(null);
    activationGeneration.current += 1;
    anchorRef.current = next;
    setAnchor(next);
  }, []);

  const isCurrentAnchor = useCallback(
    (current: Anchor): boolean => {
      const root = rootRef.current;
      return Boolean(
        root &&
          current.marker.isConnected &&
          current.cell.isConnected &&
          root.contains(current.marker) &&
          anchorCellFor(current.marker) === current.cell &&
          registeredActions.get(current.marker) === current.readAction &&
          current.readAction().identityKey === current.identityKey,
      );
    },
    [rootRef],
  );

  const measure = useCallback(
    (current: Anchor): Placement | null => {
      const root = rootRef.current;
      if (!root || !isCurrentAnchor(current)) return null;
      const cellRect = current.cell.getBoundingClientRect();
      const contentGeometry = contentGeometryFor(current.marker);
      if (!contentGeometry) return null;
      const geometry = rowGeometryFor(root, current.cell, cellRect);
      return computePlacement({
        cellRect,
        contentRect: contentGeometry.rect,
        precedingContentRect: geometry.precedingContentRect,
        tableRect: root.getBoundingClientRect(),
        stickyBoundary: geometry.stickyBoundary,
        allowTrailingPlacement: contentGeometry.hasValueControl,
        leadingLaneOccupied:
          current.marker.querySelector(CELL_DRILLDOWN_SELECTOR) != null,
      });
    },
    [isCurrentAnchor, rootRef],
  );

  const clearHover = useCallback(() => {
    clearTimer(showTimer);
    clearTimer(hideTimer);
    pendingCell.current = null;
    updateAnchor(null);
    setPlacement(null);
  }, [updateAnchor]);

  const anchorFor = useCallback(
    (marker: HTMLElement): Anchor | null => {
      const root = rootRef.current;
      const readAction = registeredActions.get(marker);
      const action = readAction?.();
      const cell = anchorCellFor(marker);
      if (
        !root ||
        !readAction ||
        !action ||
        !marker.isConnected ||
        !cell.isConnected ||
        !root.contains(marker)
      ) {
        return null;
      }
      return {
        marker,
        cell,
        readAction,
        identityKey: action.identityKey,
        displayValue: action.displayValue?.trim() || "this value",
      };
    },
    [rootRef],
  );

  const finishActivation = useCallback(
    (generation: number, result: NucleusExplainActivationOutcome) => {
      if (activationGeneration.current !== generation) return;
      activationPendingRef.current = false;
      setActivationPending(false);
      if (isRejectedNucleusExplainActivation(result)) {
        setRejectionReason(result.reason);
        setTooltipVisible(true);
        return;
      }
      clearHover();
    },
    [clearHover],
  );

  const startActivation = useCallback(
    (current: Anchor) => {
      if (activationPendingRef.current) return;
      if (!isCurrentAnchor(current)) {
        clearHover();
        return;
      }

      const currentAction = current.readAction();
      if (currentAction.disabled) {
        setTooltipVisible(true);
        return;
      }

      activationPendingRef.current = true;
      setActivationPending(true);
      setRejectionReason(null);
      setTooltipVisible(true);
      const generation = activationGeneration.current + 1;
      activationGeneration.current = generation;
      try {
        const result = currentAction.onExplain();
        if (isActivationPromise(result)) {
          void result.then(
            (outcome) => finishActivation(generation, outcome),
            () =>
              finishActivation(generation, {
                status: "rejected",
                reason: NUCLEUS_EXPLAIN_START_FAILED_REASON,
              }),
          );
          return;
        }
        finishActivation(generation, result);
      } catch {
        finishActivation(generation, {
          status: "rejected",
          reason: NUCLEUS_EXPLAIN_START_FAILED_REASON,
        });
      }
    },
    [clearHover, finishActivation, isCurrentAnchor],
  );

  const showCell = useCallback(
    (marker: HTMLElement) => {
      const next = anchorFor(marker);
      if (!next) {
        clearHover();
        return;
      }
      const nextPlacement = measure(next);
      if (!nextPlacement) {
        clearHover();
        return;
      }
      updateAnchor(next);
      setPlacement(nextPlacement);
    },
    [anchorFor, clearHover, measure, updateAnchor],
  );

  const activateMarker = useCallback(
    (marker: HTMLElement) => {
      if (activationPendingRef.current) return;
      const next = anchorFor(marker);
      if (!next) {
        clearHover();
        return;
      }
      const current = anchorRef.current;
      const isSameAnchor =
        current?.marker === next.marker &&
        current.identityKey === next.identityKey &&
        current.readAction === next.readAction;
      if (!isSameAnchor) updateAnchor(next);
      setPlacement(measure(next));
      startActivation(next);
    },
    [anchorFor, clearHover, measure, startActivation, updateAnchor],
  );

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const scheduleHide = () => {
      clearTimer(showTimer);
      clearTimer(hideTimer);
      hideTimer.current = setTimeout(clearHover, LEAVE_GRACE_MS);
    };
    const handlePointerOver = (event: PointerEvent) => {
      const marker = findExplainMarker(root, event.target);
      if (!marker) return;
      clearTimer(hideTimer);
      if (anchorRef.current?.marker === marker) return;
      clearTimer(showTimer);
      pendingCell.current = marker;
      showTimer.current = setTimeout(() => {
        if (pendingCell.current === marker) showCell(marker);
      }, HOVER_DELAY_MS);
    };
    const handlePointerOut = (event: PointerEvent) => {
      const marker = findExplainMarker(root, event.target);
      if (!marker) return;
      const related = event.relatedTarget;
      if (related instanceof Node && anchorCellFor(marker).contains(related)) {
        return;
      }
      scheduleHide();
    };
    const handleFocusIn = (event: FocusEvent) => {
      const marker = findExplainMarker(root, event.target);
      if (!marker) return;
      clearTimer(showTimer);
      clearTimer(hideTimer);
      if (anchorRef.current?.marker === marker) return;
      pendingCell.current = marker;
      showCell(marker);
    };
    const handleFocusOut = (event: FocusEvent) => {
      const marker = findExplainMarker(root, event.target);
      if (!marker) return;
      const related = event.relatedTarget;
      if (related instanceof Node && anchorCellFor(marker).contains(related)) {
        return;
      }
      scheduleHide();
    };
    const handleActivation = (event: Event) => {
      const marker = findExplainMarker(root, event.target);
      if (marker) activateMarker(marker);
    };
    const handleScroll = () => clearHover();

    root.addEventListener("pointerover", handlePointerOver);
    root.addEventListener("pointerout", handlePointerOut);
    root.addEventListener("focusin", handleFocusIn);
    root.addEventListener("focusout", handleFocusOut);
    root.addEventListener(NUCLEUS_EXPLAIN_ACTIVATE_EVENT, handleActivation);
    root.addEventListener("scroll", handleScroll, true);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      root.removeEventListener("pointerover", handlePointerOver);
      root.removeEventListener("pointerout", handlePointerOut);
      root.removeEventListener("focusin", handleFocusIn);
      root.removeEventListener("focusout", handleFocusOut);
      root.removeEventListener(
        NUCLEUS_EXPLAIN_ACTIVATE_EVENT,
        handleActivation,
      );
      root.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("scroll", handleScroll, true);
      clearTimer(showTimer);
      clearTimer(hideTimer);
    };
  }, [activateMarker, clearHover, rootRef, showCell]);

  useLayoutEffect(() => {
    if (!anchor) return;
    if (!isCurrentAnchor(anchor)) {
      clearHover();
      return;
    }
    setPlacement(measure(anchor));
    let frameId: number | null = null;
    const scheduleMeasurement = () => {
      if (frameId !== null) return;
      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        if (anchorRef.current !== anchor) return;
        if (!isCurrentAnchor(anchor)) {
          clearHover();
          return;
        }
        setPlacement(measure(anchor));
      });
    };
    const ResizeObserverCtor = window.ResizeObserver;
    const resizeObserver = ResizeObserverCtor
      ? new ResizeObserverCtor(scheduleMeasurement)
      : null;
    resizeObserver?.observe(anchor.cell);
    if (rootRef.current) resizeObserver?.observe(rootRef.current);
    window.addEventListener("resize", scheduleMeasurement);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleMeasurement);
      if (frameId !== null) window.cancelAnimationFrame(frameId);
    };
  }, [anchor, clearHover, isCurrentAnchor, measure, rootRef]);

  useEffect(() => {
    if (!anchor) return;
    const root = rootRef.current;
    if (!root || !isCurrentAnchor(anchor)) {
      clearHover();
      return;
    }
    const observer = new MutationObserver(() => {
      if (anchorRef.current === anchor && !isCurrentAnchor(anchor)) {
        clearHover();
      }
    });
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [anchor, clearHover, isCurrentAnchor, rootRef]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") clearHover();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [clearHover]);

  useLayoutEffect(() => {
    if (anchor && !isCurrentAnchor(anchor)) {
      clearHover();
    }
  });

  useEffect(
    () => () => {
      activationPendingRef.current = false;
      activationGeneration.current += 1;
    },
    [],
  );

  if (!anchor || !isCurrentAnchor(anchor)) {
    return null;
  }

  const activeAction = anchor.readAction();
  const activeDisplayValue =
    activeAction.displayValue?.trim() || anchor.displayValue;
  const actionBusy = activeAction.disabled || activationPending;
  if (!placement) {
    const feedback = activationPending
      ? NUCLEUS_EXPLAIN_STARTING_REASON
      : activeAction.disabled
        ? NUCLEUS_EXPLAIN_BUSY_REASON
        : rejectionReason
          ? `${rejectionReason} Press Enter or Space to retry.`
          : null;
    if (!feedback) return null;
    return createPortal(
      <Box data-nucleus-explain-overlay>
        <Box
          role="status"
          aria-live="polite"
          data-nucleus-explain-keyboard-feedback
          sx={{
            position: "absolute",
            width: 1,
            height: 1,
            p: 0,
            m: "-1px",
            overflow: "hidden",
            clip: "rect(0 0 0 0)",
            whiteSpace: "nowrap",
            border: 0,
          }}
        >
          {feedback}
        </Box>
      </Box>,
      document.body,
    );
  }
  const detail = activationPending
    ? NUCLEUS_EXPLAIN_STARTING_REASON
    : activeAction.disabled
      ? NUCLEUS_EXPLAIN_BUSY_REASON
      : rejectionReason
        ? `${rejectionReason} Click to retry.`
        : detailFor(activeAction.detailMode, activeDisplayValue);
  const actionLabel = rejectionReason
    ? `${activeAction.ariaLabel}. ${rejectionReason} Retry.`
    : activeAction.ariaLabel;

  const explain = (event: ReactPointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    startActivation(anchor);
  };
  const scheduleActionHide = () => {
    setTooltipVisible(false);
    clearTimer(hideTimer);
    hideTimer.current = setTimeout(clearHover, LEAVE_GRACE_MS);
  };

  return createPortal(
    <Box data-nucleus-explain-overlay>
      <Box
        data-nucleus-explain-outline
        data-testid="nucleus-explain-outline"
        sx={{
          position: "fixed",
          zIndex: 1200,
          border: `1.5px solid ${NUCLEUS_BLUE}`,
          borderRadius: "4px",
          pointerEvents: "none",
        }}
        style={{
          left: placement.outlineRect.left,
          top: placement.outlineRect.top,
          width: placement.outlineRect.width,
          height: placement.outlineRect.height,
        }}
      />
      <IconButton
        id={activeAction.actionId}
        type="button"
        data-nucleus-explain-action
        aria-label={actionLabel}
        aria-disabled={actionBusy}
        aria-busy={activationPending}
        aria-describedby={tooltipVisible ? tooltipId : undefined}
        onClick={explain}
        onPointerEnter={() => {
          clearTimer(hideTimer);
          setTooltipVisible(true);
        }}
        onPointerLeave={(event) => {
          if (document.activeElement !== event.currentTarget) {
            scheduleActionHide();
          }
        }}
        onFocus={() => {
          clearTimer(hideTimer);
          setTooltipVisible(true);
        }}
        onBlur={scheduleActionHide}
        sx={{
          position: "fixed",
          zIndex: 1201,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: ACTION_SIZE,
          height: ACTION_SIZE,
          p: 0,
          border: "1px solid #C8D8FF",
          borderRadius: "50%",
          backgroundColor: "#fff",
          color: NUCLEUS_BLUE,
          cursor: actionBusy ? "not-allowed" : "pointer",
          opacity: actionBusy ? 0.58 : 1,
          boxShadow: "0 2px 8px rgb(15 23 42 / 16%)",
          transition:
            "background-color 120ms ease, border-color 120ms ease, transform 120ms ease, box-shadow 120ms ease",
          "&:hover, &:focus-visible": actionBusy
            ? {}
            : {
                borderColor: NUCLEUS_BLUE,
                backgroundColor: "#EEF4FF",
                boxShadow: "0 3px 10px rgb(15 23 42 / 20%)",
                transform: "scale(1.04)",
              },
          "&:focus-visible": {
            outline: `2px solid ${NUCLEUS_BLUE}`,
            outlineOffset: "2px",
          },
        }}
        style={{
          left: placement.actionRect.left,
          top: placement.actionRect.top,
        }}
      >
        <AtomicLogo width={15} height={15} />
      </IconButton>
      {tooltipVisible && (
        <Box
          id={tooltipId}
          role="tooltip"
          data-nucleus-explain-tooltip
          sx={{
            position: "fixed",
            zIndex: 1202,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: "2px",
            width: TOOLTIP_WIDTH,
            height: TOOLTIP_HEIGHT,
            px: "10px",
            py: "7px",
            borderRadius: "8px",
            backgroundColor: "#111827",
            color: "#fff",
            boxShadow: "0 8px 20px rgb(15 23 42 / 24%)",
            pointerEvents: "none",
            boxSizing: "border-box",
          }}
          style={{
            left: placement.tooltipRect.left,
            top: placement.tooltipRect.top,
          }}
        >
          <Box component="span" sx={{ fontSize: 11, fontWeight: 650 }}>
            Explain with Nucleus
          </Box>
          <Box
            component="span"
            sx={{ color: "#D1D5DB", fontSize: 10, lineHeight: 1.2 }}
          >
            {detail}
          </Box>
        </Box>
      )}
    </Box>,
    document.body,
  );
};
